/**
 * The lobby — pick a world, and say who you are on the way in.
 *
 * Worlds are fetched from /api/worlds rather than bundled, so adding one to the
 * server's registry makes it joinable without shipping a new client. Each world
 * is its own page: switching means a navigation, not a teardown. Two renderers
 * that each want the whole GPU should never be alive at once, and a page unload
 * is the one teardown path neither Babylon nor Three can leak through.
 */

import { getPlayerName, setPlayerName } from "../net/identity.js";

// Imported rather than pointed at from /public, so Vite fingerprints them and a
// missing file is a build error instead of a card with a hole in it.
import artSnowflow from "../assets/worlds/snowflow.svg";
import artSandworm from "../assets/worlds/sandworm.svg";

/** Where each world lives. Server ids map to pages here and nowhere else. */
const WORLD_PAGES = {
  snowflow: "/snowflow.html",
  sandworm: "/sandworm.html",
};

/**
 * The look of each world, which is a client concern — the server's catalogue
 * says what a world *is* (name, blurb, player cap, what it needs to render) and
 * this says what it looks like on a card. A world the server adds without an
 * entry here still lists; it just gets no art, which is a degraded card rather
 * than a broken page.
 */
const WORLD_ART = {
  snowflow: { art: artSnowflow, kicker: "freeze or be frozen" },
  sandworm: { art: artSandworm, kicker: "the rain is coming" },
};

const els = {
  worlds: document.getElementById("worlds"),
  name: document.getElementById("name"),
};

let capabilities = null;

boot();

async function boot() {
  els.name.value = getPlayerName();
  els.name.addEventListener("change", () =>
    setPlayerName(els.name.value.trim() || "Player"),
  );

  capabilities = await detectCapabilities();

  let worlds;
  try {
    const res = await fetch("/api/worlds");
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    ({ worlds } = await res.json());
  } catch (err) {
    els.worlds.innerHTML =
      `<p class="error">Could not reach the server (${escapeHtml(String(err.message))}). ` +
      `Reload to try again.</p>`;
    return;
  }

  renderWorlds(worlds.filter((w) => WORLD_PAGES[w.id]));
}

/**
 * What this machine can actually render. A world the browser cannot run is
 * shown but not offered — better to say why than to hand someone a black screen
 * three seconds after they click.
 */
async function detectCapabilities() {
  const caps = { webgl: false, webgpu: false };

  try {
    const canvas = document.createElement("canvas");
    caps.webgl = !!(canvas.getContext("webgl2") ?? canvas.getContext("webgl"));
  } catch {
    caps.webgl = false;
  }

  if (navigator.gpu) {
    try {
      caps.webgpu = !!(await navigator.gpu.requestAdapter());
    } catch {
      caps.webgpu = false;
    }
  }
  return caps;
}

function renderWorlds(worlds) {
  els.worlds.replaceChildren(...worlds.map(worldCard));
}

/**
 * One world, as a poster with a caption.
 *
 * A card is a single `<button>` rather than a div with a button in it: the
 * whole poster is the target, keyboard focus lands on it once, and the browser
 * gives us the disabled state for free when the world cannot render here.
 *
 * Built node by node rather than with innerHTML — the name and blurb come off
 * the wire, and the one place this file interpolates a string into markup is
 * the fetch failure, which is escaped.
 */
function worldCard(world) {
  const supported = capabilities[world.requires] === true;
  const skin = WORLD_ART[world.id];

  const card = document.createElement("button");
  card.className = "world" + (supported ? "" : " unsupported");
  card.dataset.world = world.id;
  card.disabled = !supported;

  const shot = document.createElement("div");
  shot.className = "shot";

  if (skin) {
    const img = document.createElement("img");
    img.src = skin.art;
    // The title beside it already names the world; announcing the art again
    // just makes a screen reader say everything twice.
    img.alt = "";
    img.loading = "lazy";
    shot.appendChild(img);
  }

  if (skin?.kicker) {
    const kicker = document.createElement("div");
    kicker.className = "shot-kicker";
    kicker.textContent = skin.kicker;
    shot.appendChild(kicker);
  }

  const title = document.createElement("div");
  title.className = "shot-title";
  title.textContent = world.name;
  shot.appendChild(title);

  const body = document.createElement("div");
  body.className = "world-body";

  const blurb = document.createElement("p");
  blurb.textContent = world.blurb;

  const meta = document.createElement("p");
  meta.className = "meta";
  if (supported) {
    meta.append(
      tag(`up to ${world.maxPlayers}`),
      tag(world.requires.toUpperCase()),
      cta("enter →"),
    );
  } else {
    meta.textContent = `needs ${world.requires.toUpperCase()}, which this browser does not have`;
  }

  body.append(blurb, meta);
  card.append(shot, body);

  if (supported) card.addEventListener("click", () => enter(world.id));
  return card;
}

function tag(text) {
  const el = document.createElement("span");
  el.className = "tag";
  el.textContent = text;
  return el;
}

function cta(text) {
  const el = document.createElement("span");
  el.className = "cta";
  el.textContent = text;
  return el;
}

/**
 * Opening a world is now just opening its page — no code. Starting a room and
 * joining one both happen inside the world, where the panel already knows which
 * world a code has to belong to, so the lobby no longer has to look one up.
 */
function enter(worldId) {
  location.href = WORLD_PAGES[worldId];
}

function escapeHtml(s) {
  return s.replace(
    /[&<>"']/g,
    (c) =>
      ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[
        c
      ],
  );
}
